<template>
  <div :class="['opening-hours', { open: isOpen }]" v-html="openHoursText" />
</template>

<script lang="ts">
import {
  WeeklyOpeningHours,
  DailyOpeningHours,
  OpeningHoursDataModel
} from '../types/PersonalAssistance';
import { defineComponent, PropType } from 'vue';
import moment from 'moment';

export default defineComponent({
  name: 'OpeningHours',
  props: {
    openingHours: {
      type: Object as PropType<WeeklyOpeningHours>,
      default() {
        return {
          monday: {
            open: null,
            close: null
          },
          tuesday: {
            open: null,
            close: null
          },
          wednesday: {
            open: null,
            close: null
          },
          thursday: {
            open: null,
            close: null
          },
          friday: {
            open: null,
            close: null
          },
          saturday: {
            open: null,
            close: null
          },
          sunday: {
            open: null,
            close: null
          }
        };
      }
    },
    openDescription: {
      type: String,
      default() {
        return '';
      }
    },
    closedDescription: {
      type: String,
      default() {
        return '';
      }
    }
  },
  data(): OpeningHoursDataModel {
    return {
      isOpen: null,
      dayToday: '',
      timeNow: '',
      nextOpenDay: '',
      nextOpenDayDanish: '',
      hoursFormat: 'HH:mm:ss',
      dayFormat: 'dddd',
      openHoursText: ''
    };
  },
  computed: {
    todayHours(): DailyOpeningHours {
      return this.getDailyOpeningHours(this.dayToday);
    },
    showComponent(): boolean {
      return this.isOpen !== null && this.openHoursText !== '';
    }
  },
  mounted() {
    window.setInterval(() => {
      this.updateTimeNow();
      this.updateIsOpen();
    }, 1000);
  },
  methods: {
    updateTimeNow() {
      this.timeNow = moment().locale('en').format(this.hoursFormat);
      this.dayToday = moment().locale('en').format(this.dayFormat).toLowerCase();
    },
    updateIsOpen() {
      if (this.todayHours === null) {
        this.isOpen = false;
        this.getNextOpenDay();
        return;
      }
      const timeNow = moment(this.timeNow, this.hoursFormat);
      const openingTime = moment(this.todayHours.open, this.hoursFormat);
      const closingTime = moment(this.todayHours.close, this.hoursFormat);

      this.isOpen = timeNow.isAfter(openingTime) && timeNow.isBefore(closingTime);
      this.openHoursText =
        this.dayToday !== '' && this.nextOpenDay !== '' ? this.getOpenHoursText() : '';
      this.getNextOpenDay();
    },
    getNextOpenDay() {
      let daysAhead = 1;
      let nextOpenDayFound = false;
      Object.entries(this.openingHours).forEach(() => {
        if (nextOpenDayFound) {
          return;
        }
        const nextDayMoment = moment().locale('en').add(daysAhead, 'days');
        const nextDay = nextDayMoment.format(this.dayFormat).toLowerCase();
        const nextDayOpeningHours = this.getDailyOpeningHours(nextDay);
        const todayOpeningHours = this.getDailyOpeningHours(this.dayToday);
        const timeNow = moment(this.timeNow, this.hoursFormat);
        const openingTimeToday = moment(todayOpeningHours?.open, this.hoursFormat);

        if (
          nextDayOpeningHours === null ||
          nextDayOpeningHours.open === null ||
          nextDayOpeningHours.close === null
        ) {
          daysAhead++;
          return;
        }
        this.nextOpenDay =
          todayOpeningHours.open !== null && timeNow.isBefore(openingTimeToday)
            ? this.dayToday
            : nextDay;
        this.nextOpenDayDanish =
          todayOpeningHours.open !== null && timeNow.isBefore(openingTimeToday)
            ? timeNow.locale('da').format(this.dayFormat)
            : nextDayMoment.locale('da').format(this.dayFormat);
        nextOpenDayFound = true;
      });
    },
    getDailyOpeningHours(day: string) {
      return (this.openingHours as Record<string, DailyOpeningHours>)[day];
    },
    getOpenHoursText() {
      if (this.isOpen && this.openDescription !== '') {
        return this.openDescription;
      }
      if (!this.isOpen && this.closedDescription) {
        return this.closedDescription;
      }
      const openHours = this.isOpen
        ? this.getDailyOpeningHours(this.dayToday)
        : this.getDailyOpeningHours(this.nextOpenDay);
      if (!this.isOpen && openHours.open !== null) {
        return `Åbner igen ${this.nextOpenDayDanish} kl. ${this.formatTime(openHours.open)}`;
      }
      if (this.isOpen && openHours.close !== null) {
        return `Åben til kl. ${this.formatTime(openHours.close)}`;
      }
      return '';
    },
    formatTime(time: string) {
      return time.replaceAll(':00', '').replace(/^0+/, '');
    }
  }
});
</script>

<style lang="scss" scoped>
@import 'src/styles/all';

.opening-hours {
  display: flex;
  align-items: center;
  font-size: rem-calc(16px);
  font-weight: 500;
  position: relative;
  padding-left: rem-calc(34px);

  &:before {
    content: '';
    height: rem-calc(24px);
    width: rem-calc(24px);
    background-color: $medium-gray;
    border-radius: 50%;
    transition: background-color 0.5s ease;
    position: absolute;
    left: 0;
  }

  &.open {
    &:before {
      background-color: #0b7341;
    }
  }
}
</style>
